import { Tenant } from '@generated/tcea/models/tenant';
import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  FormGroupDirective,
  NgForm,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { DecoratorData } from '@components/item-decorator/index';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { noLeadingWhiteSpace } from '@helpers/Validators';
import {
  ChangeSetItemType,
  FormattedChangesForItem,
  ItemChanges,
} from '@interfaces/change-sets/1.0';
import { Decorator } from '@interfaces/decorator/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, skipWhile, take, takeUntil } from 'rxjs/operators';
import { ChangeSetsService } from '@services/change-sets/1.0/change-sets.service';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { CapitalizeService } from '@services/utilities/1.0/capitalize.service';
import { IsEqualService } from '@services/utilities/1.0/is-equal.service';

import { ErrorStateMatcher } from '@angular/material/core';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { LoadingComponent } from '../loading/1.0/loading.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { ChangeSetNoticeComponent } from '../change-set-notice/change-set-notice.component';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

export class InstantErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    return !!(control && control.invalid);
  }
}

@Component({
  selector: 'txp-item-decorator-cva',
  templateUrl: './item-decorator-cva.component.html',
  styleUrls: ['./item-decorator-cva.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemDecoratorCvaComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => ItemDecoratorCvaComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    MatAccordion,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    ChangeSetNoticeComponent,
    MatExpansionPanelDescription,
    MatFormField,
    NgClass,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    TranslateModule,
  ],
})
export class ItemDecoratorCvaComponent
  implements ControlValueAccessor, OnChanges, OnDestroy, AsyncValidator
{
  @Input() decoratorData: DecoratorData;
  @Input() itemChanges: ItemChanges;
  @Input() selectedVersion: ChangeSetItemType;
  @Input() loading: boolean;
  @Input() titleMaxLength: number = 500;
  @Input() descriptionMaxLength: number = 500;
  @Input() preventDefaultLanguageBehavior: boolean;
  @Input() forceTextArea: boolean = false;
  @Input() brand: Tenant;
  @Input() readOnly;

  onChangeFn: any;
  onTouchedFn: any;
  disabled: boolean;
  form: UntypedFormGroup;
  value: any;
  valueBeforeChange: any;
  ignoreChange: boolean;
  loadingType = LoadingType;
  loadingColor = LoadingColor;
  originalData: any;
  matcher = new InstantErrorStateMatcher();

  private unsubscribe = new Subject<void>();

  constructor(
    private changeSetsService: ChangeSetsService,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(): void {
    if (this.decoratorData) {
      this.unsubscribe.next();
      this.form = this.getForm(this.decoratorData);
      this.setDisabledState(this.disabled);
      this.setFormValues(this.value);
      this.applyItemChangeDecoratorValues();
      this.valueBeforeChange = this.form.value;
      this.form.valueChanges
        .pipe(
          takeUntil(this.unsubscribe),
          filter(() => !this.ignoreChange),
        )
        .subscribe((value) => {
          const decorators = this.getDecorators(value);

          if (!this.valueBeforeChange) {
            this.valueBeforeChange = this.form.value;
          }

          if (this.onChangeFn && !IsEqualService.isEqual(this.originalData, value)) {
            this.onChangeFn(decorators);
          }
        });
    }
  }

  private applyItemChangeDecoratorValues(): void {
    const isChangeSetVersion =
      this.selectedVersion === ChangeSetItemType.DRAFT ||
      this.selectedVersion === ChangeSetItemType.SCHEDULED;
    const decoratorChanges = this.itemChanges?.[this.selectedVersion]?.decorators;

    if (!isChangeSetVersion || !this.form || !decoratorChanges?.length) {
      return;
    }

    this.ignoreChange = true;
    decoratorChanges.forEach((decorator) => {
      const keyCtrl = this.form.get(decorator.key) as UntypedFormGroup;
      const fieldCtrl = keyCtrl?.controls?.[decorator.field];

      if (fieldCtrl && decorator.value !== undefined && decorator.value !== null) {
        fieldCtrl.setValue(decorator.value, { emitEvent: false });
      }
    });
    this.originalData = this.form.value;
    this.ignoreChange = false;
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  hasLanguageChanges(
    key: { property: string; label: string },
    versionToCompare: string,
    itemChanges: FormattedChangesForItem,
  ): boolean {
    const { decorators } = itemChanges || {};
    const formForControl = this.form.get(key.property);
    const sameVersion = versionToCompare === this.selectedVersion;
    const fieldMap = this.decoratorData.fields.reduce((obj, field) => {
      obj[field.property] = field;
      return obj;
    }, {});

    return (
      !!decorators?.find((decorator) => {
        return fieldMap[decorator.field] && decorator.key === key.property;
      }) ||
      (sameVersion && formForControl.dirty)
    );
  }

  hasDecoratorChanged(
    field: string,
    controlKey: string,
    versionToCompare: string,
    itemChanges: FormattedChangesForItem,
  ): boolean {
    const { decorators } = itemChanges || {};
    const formForControl = this.form.get(`${controlKey}.${field}`);
    const sameVersion = versionToCompare === this.selectedVersion;

    return (
      !!decorators?.find((decorator) => {
        return decorator.field === field && decorator.key === controlKey;
      }) ||
      (sameVersion && formForControl?.dirty)
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.form) {
      this.disabled ? this.form.disable() : this.form.enable();
    }
  }

  writeValue(obj: any): void {
    const currentFieldsMap = Object.keys(this.form?.value || {}).reduce((obj, languageKey) => {
      const languageValue = this.form.value[languageKey];

      Object.keys(languageValue).forEach((key) => {
        obj[`${key}_${languageKey}`] = languageValue[key];
      });

      return obj;
    }, {});
    const dirtyFieldsMap =
      obj?.reduce((obj, decorator) => {
        obj[`${decorator.field}_${decorator.key}`] = decorator.value;
        return obj;
      }, {}) || {};
    const newFieldsMap = DeepCloneService.clone(dirtyFieldsMap);
    const resetFieldsMap = {};

    if (this.value?.length) {
      this.value.forEach((decorator) => {
        const decoratorKey = `${decorator.field}_${decorator.key}`;

        if (IsEqualService.isEqual(dirtyFieldsMap[decoratorKey], decorator.value)) {
          delete dirtyFieldsMap[decoratorKey];
        }
      });

      if (obj?.length) {
        Object.keys(dirtyFieldsMap).forEach((key) => {
          if (!dirtyFieldsMap.hasOwnProperty(key)) {
            resetFieldsMap[key] = true;
          }
        });

        Object.keys(currentFieldsMap).forEach((key) => {
          if (!newFieldsMap.hasOwnProperty(key)) {
            resetFieldsMap[key] = true;
          }
        });
      }
    }

    this.value = DeepCloneService.clone(obj);
    this.ignoreChange = true;
    const innerFormIsDirty = !!this.form?.dirty;
    this.setFormValues(obj, null, resetFieldsMap);
    if (!innerFormIsDirty) {
      this.form?.markAsPristine();
    }
    this.ignoreChange = false;
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (this.form?.status !== 'PENDING') {
      return of(
        this.form?.valid
          ? undefined
          : {
              invalidForm: {
                valid: false,
                message: CapitalizeService.capitalize(
                  this.translateService.instant('form.error.decoratorFieldsAreInvalid'),
                ),
              },
            },
      );
    } else {
      const obs = this.form.statusChanges.pipe(
        takeUntil(this.unsubscribe),
        skipWhile((status) => status === 'PENDING'),
        take(1),
        map((d) => {
          return this.form.valid
            ? undefined
            : {
                invalidForm: {
                  valid: false,
                  message:
                    ' ' + this.translateService.instant('form.error.decoratorFieldsAreInvalid'),
                },
              };
        }),
      );
      return obs;
    }
  }

  /**
   * Returns a FormGroup for the given DecoratorData with key values
   * as the outer FormGroups and field values as inner FormGroups
   * for each key FormGroup.
   *
   * @param data - DecoratorData
   */
  getForm(data: DecoratorData): UntypedFormGroup {
    const form = new UntypedFormGroup({});

    for (const key of data.keys) {
      const group = new UntypedFormGroup({});
      form.addControl(key.property, group);

      for (const field of data.fields) {
        group.addControl(field.property, new UntypedFormControl('', [noLeadingWhiteSpace]));
      }
    }
    return form;
  }

  /**
   * Returns true if field should be readOnly, false otherwise
   * @param {DecoratorData['keys'][0]} key - language key
   * @param field - field property name
   * @return {Boolean} - true if field should be readOnly, false otherwise
   */
  fieldIsReadOnly(key, field?: string) {
    // Subtitle fields should always be editable since they're only managed through decorators
    if (field === 'subTitle') {
      return this.readOnly;
    }

    return (
      (!this.preventDefaultLanguageBehavior && key.property === this.brand?.language) ||
      this.readOnly
    );
  }

  /**
   * Returns true if the object has any falsy property values or false otherwise
   *
   * @param obj - Object to check properties
   * @param key - Language key to determine which fields are required
   */
  hasBlank(obj: any, key?: string): boolean {
    for (const prop in obj) {
      // Subtitle fields should always be editable since they're only managed through decorators
      if (prop === 'subTitle') {
        continue;
      }
      if (!obj[prop]?.trim()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Sets the appropriate form values based on this Decorator array
   *
   * @param decorators - Decorators to set values for
   * @param dirtyFieldsMap - Map for decorator fields that are dirty
   * @param resetFieldsMap - Map for decorator fields that should be marked as pristine and be deleted
   */
  setFormValues(
    decorators: Decorator[],
    dirtyFieldsMap: { [key: string]: boolean } = null,
    resetFieldsMap: { [key: string]: boolean } = null,
  ) {
    if (this.form) {
      if (decorators) {
        for (const decorator of decorators) {
          const decoratorKey = `${decorator.field}_${decorator.key}`;
          const shouldSetValue = !dirtyFieldsMap || dirtyFieldsMap.hasOwnProperty(decoratorKey);
          const keyCtrl = this.form.get(decorator.key) as UntypedFormGroup;
          // use controls map instead of get function due to field names potentially containing periods
          const fieldCtrl = keyCtrl?.controls[decorator.field];
          if (fieldCtrl) {
            if (keyCtrl && shouldSetValue) {
              const differentValue = fieldCtrl.value !== decorator.value;

              fieldCtrl.setValue(decorator.value);

              if (dirtyFieldsMap && differentValue) {
                fieldCtrl.markAsDirty();
              }
            }

            if (this.changeSetsService.isResetting) {
              fieldCtrl.markAsPristine();
            }
          }
        }
      }

      if (resetFieldsMap) {
        Object.keys(resetFieldsMap).forEach((pathName) => {
          const pathSplit = pathName.split('_');
          const formForPath = this.form.get(`${pathSplit[1]}.${pathSplit[0]}`);

          if (formForPath) {
            formForPath.reset('');
            formForPath.markAsPristine();
          }
        });
      }

      this.disabled ? this.form.disable() : this.form.enable();
      this.originalData = this.form.value;
    }
  }

  /**
   * Converts the given form value object into a Decorator array
   *
   * @param formValue - nested object of keys and fields
   */
  getDecorators(formValue: any): Decorator[] {
    const decorators: Decorator[] = [];
    for (const key in formValue) {
      if (formValue.hasOwnProperty(key)) {
        const fields = formValue[key];
        for (const field in fields) {
          if (fields.hasOwnProperty(field)) {
            const value = fields[field];
            decorators.push({ field, key, value });
          }
        }
      }
    }
    return decorators;
  }
}
